import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LinkRaceRequest, SaveVoteGoalRequest } from '@signalvote/shared';
import { ErrorCode } from '@signalvote/shared';
import { ApiError, getApiClient } from '@signalvote/api-client';
import { authService } from '../../services/authService';
import { useCampaignStore } from '../../stores';
import { queryKeys } from '../../lib/queryKeys';

const api = getApiClient();

/** The campaign's Race with each stage's current goal, or null when the campaign has no Race yet. */
export function useCampaignRace() {
  const { selectedCampaign } = useCampaignStore();
  return useQuery({
    queryKey: queryKeys.racePicture.campaignRace(),
    queryFn: async () => {
      try {
        return await api.campaignRace.get();
      } catch (e) {
        if (e instanceof ApiError && e.code === ErrorCode.CampaignRaceNotLinked) return null;
        throw e;
      }
    },
    enabled: authService.isAuthenticated() && !!selectedCampaign,
  });
}

/** Searches one state's Races for what the candidate typed. */
export function useRaceSearch(state: string, phrase: string) {
  const q = phrase.trim();
  return useQuery({
    queryKey: queryKeys.racePicture.search(state, q),
    queryFn: () => api.races.search({ state, q }),
    enabled: !!state && q.length > 0,
    staleTime: 5 * 60_000,
  });
}

/** One Race from the public catalog, before any campaign links it. */
export function useRace(raceId: string | null) {
  return useQuery({
    queryKey: queryKeys.racePicture.race(raceId ?? ''),
    queryFn: () => api.races.get(raceId!),
    enabled: !!raceId,
    staleTime: 5 * 60_000,
  });
}

export function useLinkRace() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: LinkRaceRequest) => api.campaignRace.link(request),
    onSuccess: (written) => queryClient.setQueryData(queryKeys.racePicture.campaignRace(), written.link),
  });
}

/** Saves one stage's goal. The refetch after it carries the stage's fresh token, so a second save doesn't false-conflict. */
export function useSaveVoteGoal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: SaveVoteGoalRequest) => api.campaignRace.saveGoal(request),
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.racePicture.campaignRace() }),
  });
}
